import React from 'react';

/**
 * Parse text and convert URLs, mentions, and hashtags into clickable links
 */
export const linkifyText = (text, options = {}) => {
  if (!text) return null;
  
  const {
    onMentionClick,
    onHashtagClick,
    className = 'text-inherit',
  } = options;
  
  // Regex patterns
  const urlPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/gi;
  const mentionPattern = /@(\w+)/g;
  const hashtagPattern = /#(\w+)/g;
  
  // Split text by URLs first
  const parts = [];
  let lastIndex = 0;
  let match;
  
  // Combined regex for all patterns
  const combinedPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])|(@\w+)|(#\w+)/gi;
  
  while ((match = combinedPattern.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }
    
    const matchedText = match[0];
    
    if (matchedText.startsWith('http')) {
      // URL
      parts.push({
        type: 'url',
        content: matchedText,
        href: matchedText,
      });
    } else if (matchedText.startsWith('@')) {
      // Mention
      parts.push({
        type: 'mention',
        content: matchedText,
        username: matchedText.slice(1),
      });
    } else if (matchedText.startsWith('#')) {
      // Hashtag
      parts.push({
        type: 'hashtag',
        content: matchedText,
        tag: matchedText.slice(1),
      });
    }
    
    lastIndex = match.index + matchedText.length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }
  
  // Render parts
  return parts.map((part, idx) => {
    switch (part.type) {
      case 'url':
        return (
          <a
            key={idx}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline break-all"
            onClick={(e) => e.stopPropagation()}
            data-testid={`link-${idx}`}
          >
            {part.content}
          </a>
        );
      case 'mention':
        return (
          <a
            key={idx}
            href={`/profile/${part.username}`}
            className="text-primary hover:underline font-medium"
            onClick={(e) => {
              if (onMentionClick) {
                e.preventDefault();
                onMentionClick(part.username);
              }
            }}
            data-testid={`mention-${part.username}`}
          >
            {part.content}
          </a>
        );
      case 'hashtag':
        return (
          <a
            key={idx}
            href={`/explore?tag=${part.tag}`}
            className="text-primary hover:underline font-medium"
            onClick={(e) => {
              if (onHashtagClick) {
                e.preventDefault();
                onHashtagClick(part.tag);
              }
            }}
            data-testid={`hashtag-${part.tag}`}
          >
            {part.content}
          </a>
        );
      default:
        return <span key={idx}>{part.content}</span>;
    }
  });
};

/**
 * LinkifiedText component - renders text with clickable links, mentions, and hashtags
 */
const LinkifiedText = ({ text, className = '', onMentionClick, onHashtagClick }) => {
  return (
    <span className={className}>
      {linkifyText(text, { onMentionClick, onHashtagClick, className })}
    </span>
  );
};

export default LinkifiedText;
